import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Mail, ArrowLeft, Key } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
export default function ForgotPasswordPage() {
  const { t } = useAppTranslation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState<'email' | 'verify' | 'reset'>('email');

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // TODO: Implement API call to send password reset code
      const response = await fetch('https://weavein-api.onrender.com/api/member-portal/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to send reset code' }));
        throw new Error(error.detail || 'Failed to send reset code');
      }

      const result = await response.json();
      setSuccess(`A 6-digit verification code has been sent to ${result.phone_number_masked || 'your phone'}`);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code. Please check your email and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // TODO: Implement API call to verify code
      const response = await fetch('https://weavein-api.onrender.com/api/member-portal/password-reset/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Invalid verification code' }));
        throw new Error(error.detail || 'Invalid verification code');
      }

      setSuccess('Code verified! Please enter your new password.');
      setStep('reset');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      // TODO: Implement API call to reset password
      const response = await fetch('https://weavein-api.onrender.com/api/member-portal/password-reset/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to reset password' }));
        throw new Error(error.detail || 'Failed to reset password');
      }

      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        window.location.href = '/member/login';
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center">
              <Key className="w-6 h-6 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {step === 'email' && 'Reset Password'}
            {step === 'verify' && 'Verify Code'}
            {step === 'reset' && 'New Password'}
          </CardTitle>
          <CardDescription className="text-center">
            {step === 'email' && "Enter your email to receive a verification code"}
            {step === 'verify' && "Enter the 6-digit code sent to your phone"}
            {step === 'reset' && "Create a new password for your account"}
          </CardDescription>
        </CardHeader>

        {step === 'email' && (
          <form onSubmit={handleSendCode}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-50 border-green-200 text-green-800">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('forgot-password.email_address', 'Email Address')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('forgot-password.sending_code', 'Sending Code...')}
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </Button>

              <Link
                to="/member/login"
                className="flex items-center justify-center text-sm text-teal-600 hover:text-teal-700"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('forgot-password.back_to_login', 'Back to login')}
              </Link>
            </CardFooter>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifyCode}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-50 border-green-200 text-green-800">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="code">{t('forgot-password.verification_code', 'Verification Code')}</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder={t('forgot-password.enter_6digit_code', 'Enter 6-digit code')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={loading}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading || code.length !== 6}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('forgot-password.verifying', 'Verifying...')}
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStep('email')}
                disabled={loading}
              >
                {t('forgot-password.resend_code', 'Resend Code')}
              </Button>
            </CardFooter>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-50 border-green-200 text-green-800">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('forgot-password.new_password', 'New Password')}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder={t('forgot-password.enter_new_password', 'Enter new password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  minLength={8}
                />
                <p className="text-xs text-gray-500">
                  {t('forgot-password.password_must_be_at_least', 'Password must be at least 8 characters long')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('forgot-password.confirm_password', 'Confirm Password')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder={t('forgot-password.confirm_new_password', 'Confirm new password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('forgot-password.resetting_password', 'Resetting Password...')}
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>

      <div className="fixed bottom-4 right-4 text-sm text-gray-500">
        <Link to="/member/home" className="hover:text-gray-700">
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
